import { PublicJobsPage, PublicJobPage } from '@/features/careers/pages/PublicJobsPage';
import type { ReactNode } from 'react';

import { Lock } from 'lucide-react';
import { Navigate, Outlet, createBrowserRouter, useLocation } from 'react-router-dom';

import type { PermissionModule } from '@/api/schema';
import { AppShell } from '@/components/layout/AppShell';
import { Page } from '@/components/layout/Page';
import { EmptyState } from '@/components/ui/EmptyState';
import { Spinner } from '@/components/ui/Spinner';
import { useCurrentUser } from '@/features/auth/api';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { SetPasswordPage } from '@/features/auth/pages/SetPasswordPage';
import { CandidateDetailPage } from '@/features/candidates/pages/CandidateDetailPage';
import { CandidateListPage } from '@/features/candidates/pages/CandidateListPage';
import { CareersPage } from '@/features/careers/pages/CareersPage';
import { CareersApplicationsPage } from '@/features/careers/pages/CareersApplicationsPage';
import { FlystBoardPage } from '@/features/screenings/pages/FlystBoardPage';
import { AtsCandidatesPage } from '@/features/ats/pages/AtsCandidatesPage';
import { AuditLogPage } from '@/features/audit/pages/AuditLogPage';
import { DashboardPage } from '@/features/dashboard/pages/DashboardPage';
import { CalendarPage } from '@/features/meetings/pages/CalendarPage';
import { MeetingLogPage } from '@/features/meetings/pages/MeetingLogPage';
import { RsrBoardPage } from '@/features/screenings/pages/RsrBoardPage';
import { CompanyPage } from '@/features/company/pages/CompanyPage';
import { RolesPage } from '@/features/roles/pages/RolesPage';
import { UsersPage } from '@/features/users/pages/UsersPage';
import { PendingScreen } from '@/features/workspace/PendingScreen';
import { can } from '@/lib/permissions';

import { ALL_ITEMS, DETAIL_ROUTES, landingPathFor } from './navigation';

/** Screens that are built. Anything absent renders a PendingScreen naming its phase. */
const SCREENS: Record<string, ReactNode> = {
  '/candidates': <CandidateListPage />,
  '/careers': <CareersPage />,
  '/careers/applications': <CareersApplicationsPage />,
  '/screenings/flyst': <FlystBoardPage />,
  '/screenings/rsr': <RsrBoardPage />,
  '/meetings/calendar': <CalendarPage />,
  '/meetings/log': <MeetingLogPage />,
  '/ats': <AtsCandidatesPage />,
  '/candidates/dashboard': <DashboardPage />,
  '/settings/audit': <AuditLogPage />,
  '/settings/users': <UsersPage />,
  '/settings/roles': <RolesPage />,
  '/settings/company': <CompanyPage />,
};

/** Record screens, keyed by their pattern. Same shape and same fallback as SCREENS. */
const DETAIL_SCREENS: Record<string, ReactNode> = {
  '/candidates/:id': <CandidateDetailPage />,
};

export const LOGIN_PATH = '/login';
/** Where a signed-in user goes when they have not asked for anywhere specific. */
export const HOME_PATH = '/candidates/dashboard';

/**
 * Held while we find out whether there is a session.
 *
 * Neither the app nor the login screen may render before that is known: showing
 * login first would flash the sign-in form at someone who is already signed in, and
 * showing the app first would flash a shell that is about to be taken away.
 */
function Resolving() {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minBlockSize: '100vh' }} role="status">
      <Spinner size={22} label="Loading Flyst" />
    </div>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { data: user, isPending } = useCurrentUser();
  const location = useLocation();

  if (isPending) return <Resolving />;

  if (!user) {
    // The intended route travels with the redirect, so signing in returns the user to
    // where they were going rather than dropping them on the dashboard.
    return <Navigate to={LOGIN_PATH} state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

function RedirectIfSignedIn({ children }: { children: ReactNode }) {
  const { data: user, isPending } = useCurrentUser();
  const location = useLocation();

  if (isPending) return <Resolving />;

  if (user) {
    // The redirect lives here and nowhere else.
    //
    // The login screen used to navigate on success itself, and this guard raced it:
    // the moment the mutation seeded the cache, this ran first and sent the user to
    // the dashboard, throwing away the route they had actually asked for. One place
    // decides, so there is nothing to race.
    const intended = (location.state as { from?: { pathname: string } } | null)?.from?.pathname;
    return <Navigate to={intended ?? landingPathFor(user) ?? HOME_PATH} replace />;
  }

  return <>{children}</>;
}

/**
 * A screen the user's role does not allow.
 *
 * Shown rather than redirected away. A silent bounce to the dashboard reads as the
 * link being broken; this says plainly that the account lacks the permission, which
 * is something an administrator can act on.
 */
function NotPermitted() {
  return (
    <Page>
      <EmptyState
        icon={<Lock size={21} />}
        title="You do not have access to this screen"
        body="Your role does not include it. An administrator can change that in Settings."
      />
    </Page>
  );
}

function RequirePermission({
  module,
  additionalModules = [],
  children,
}: {
  module: PermissionModule;
  additionalModules?: PermissionModule[];
  children: ReactNode;
}) {
  const { data: user } = useCurrentUser();
  return can(user, module) && additionalModules.every((required) => can(user, required)) ? (
    <>{children}</>
  ) : (
    <NotPermitted />
  );
}

/** The shell, with whichever screen the route resolved to inside it. */
function WorkspaceLayout() {
  const { data: user } = useCurrentUser();
  if (!user) return null;

  return (
    <AppShell user={user}>
      <Outlet />
    </AppShell>
  );
}

function LandingRedirect() {
  const { data: user } = useCurrentUser();
  return <Navigate to={landingPathFor(user) ?? HOME_PATH} replace />;
}

/**
 * A data router, not `<BrowserRouter>`.
 *
 * `useBlocker`, which is how a screen with unsaved changes stops a navigation, only
 * exists on a data router. Nothing else about the tree changes.
 */
export const router = createBrowserRouter([
  { path: '/jobs', element: <PublicJobsPage /> },
  { path: '/jobs/:id', element: <PublicJobPage /> },
  {
    path: LOGIN_PATH,
    element: (
      <RedirectIfSignedIn>
        <LoginPage />
      </RedirectIfSignedIn>
    ),
  },
  {
    // Deliberately public and outside RedirectIfSignedIn: the recipient of an
    // invitation cannot sign in yet, and an administrator checking the link may still
    // hold a session of their own.
    path: '/set-password',
    element: <SetPasswordPage />,
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <WorkspaceLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <LandingRedirect /> },

      // Both spreads come from navigation.ts, so no path is written by hand in this
      // file. A navigable screen cannot be reachable but unlisted or listed but
      // unreachable; a record page is deliberately reachable and unlisted, which is
      // what the second registry is for.
      ...ALL_ITEMS.map(({ item }) => ({
        path: item.path.slice(1),
        element: (
          <RequirePermission
            module={item.module}
            additionalModules={item.additionalModules ?? []}
          >
            {SCREENS[item.path] ?? <PendingScreen />}
          </RequirePermission>
        ),
      })),

      ...DETAIL_ROUTES.map((route) => ({
        path: route.path.slice(1),
        element: (
          <RequirePermission module={route.module}>
            {DETAIL_SCREENS[route.path] ?? <PendingScreen />}
          </RequirePermission>
        ),
      })),

      {
        path: '*',
        element: (
          <Page>
            <EmptyState
              title="That screen does not exist"
              body="Check the address, or pick something from the navigation."
            />
          </Page>
        ),
      },
    ],
  },
]);
